import {useRef} from 'react';
// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useBoolean, useOnMount} from 'shared/web';

import {useNavigationDataApi} from '../api/useNavigationDataApi';
import type {DetailPaginationItem} from '../types/DetailPaginationItem';
import type {RowControlFetchStrategy} from '../types/RowControlFetchStrategy';
import {loadDetailPaginationItems} from '../utils/loadDetailPaginationItems';
import {getFormattedSourceSearchParams} from '../utils/url/getFormattedSourceSearchParams';
import {setSourceSearchParamsByGridCode} from '../utils/url/setSourceSearchParamsByGridCode';

const DETAIL_PAGINATION_LIMIT = 50;
const ID_FETCH_STRATEGY: RowControlFetchStrategy = {type: 'id'};

export function useDetailPagination(
  onChange: (
    entityIndex: string,
    sourceSearchParams: string,
    additionalRouteParams?: Record<string, string>
  ) => void,
  fetchStrategy: RowControlFetchStrategy = ID_FETCH_STRATEGY
) {
  // searchParams subscription ensures the component re-renders when the URL changes,
  // which re-derives `page` from getFormattedSourceSearchParams().
  const [searchParams] = useSearchParams();
  void searchParams; // consumed only for its re-render side-effect

  const ids = useRef<DetailPaginationItem[] | null>(null);
  const idsSource = useRef('');
  const isMounted = useRef(false);
  const requestVersion = useRef(0);

  // Snapshot the URL-derived source params ONCE for the render-time reads below. Re-derived
  // on every render (searchParams subscription forces it). Callbacks below still call
  // getFormattedSourceSearchParams() directly so they capture current URL state at invocation.
  const sourceParams = getFormattedSourceSearchParams();

  // Derived from URL — no useState needed. Re-derived when searchParams change triggers re-render.
  const page = Number(sourceParams.rowIndex);

  const [isInvalid, invalidate, validate] = useBoolean();
  const [isLoading, startLoading, stopLoading] = useBoolean();

  const http = useNavigationDataApi(sourceParams.gridCode);
  const sourceKey = JSON.stringify([sourceParams.gridCode, sourceParams.queryId, fetchStrategy]);

  const getNavigationContext = () =>
    JSON.stringify([window.location.href, getFormattedSourceSearchParams()]);

  const fetchIds = (centerPage?: number) => {
    if (isNilOrEmpty(sourceParams.gridCode) || isNilOrEmpty(sourceParams.queryId)) {
      return;
    }
    const center = centerPage ?? page;
    const offset = center - Math.round(DETAIL_PAGINATION_LIMIT / 2);
    const version = ++requestVersion.current;
    const context = getNavigationContext();
    const isCurrentRequest = () =>
      isMounted.current && version === requestVersion.current && context === getNavigationContext();

    startLoading();
    return loadDetailPaginationItems(
      http,
      sourceParams.queryId,
      offset > 0 ? offset : 0,
      DETAIL_PAGINATION_LIMIT,
      fetchStrategy
    )
      .then((response) => {
        if (!isCurrentRequest()) {
          return false;
        }
        ids.current = response ?? [];
        idsSource.current = sourceKey;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        if (isMounted.current && version === requestVersion.current) {
          stopLoading();
        }
      });
  };

  useOnMount(
    () => {
      isMounted.current = true;
      // A list can change its membership or ordering without changing its query ID.
      // Re-entering a detail must therefore read a fresh navigation window.
      void fetchIds();
    },
    () => {
      isMounted.current = false;
      requestVersion.current += 1;
    }
  );

  const navigateToPage = (newPage: number) => {
    const foundEntity = ids.current?.find((id) => id.rowNumber === newPage);
    const foundEntityId = foundEntity?.id;

    if (isNilOrEmpty(foundEntityId)) {
      invalidate();
      return;
    }

    if (fetchStrategy.type === 'data' && isNilOrEmpty(foundEntity?.additionalRouteParams)) {
      invalidate();
      return;
    }

    const sourceSearchParams = setSourceSearchParamsByGridCode(
      getFormattedSourceSearchParams().gridCode,
      String(newPage),
      true
    );

    const initialPath = window.location.pathname + window.location.search;

    if (typeof foundEntityId !== 'string') {
      return;
    }

    if (fetchStrategy.type === 'data') {
      onChange(foundEntityId, sourceSearchParams, foundEntity?.additionalRouteParams);
    } else {
      onChange(foundEntityId, sourceSearchParams);
    }

    const currentPathAfter = window.location.pathname + window.location.search;
    const wasNavigationBlocked = currentPathAfter === initialPath;

    if (wasNavigationBlocked) {
      return;
    }

    validate();
  };

  const onPageChange = (newPage: number) => {
    if (!isMounted.current) {
      return;
    }
    requestVersion.current += 1;
    if (
      idsSource.current === sourceKey &&
      ids.current?.some((item) => item.rowNumber === newPage)
    ) {
      stopLoading();
      navigateToPage(newPage);
      return;
    }

    // Resolve the next window before navigating. Otherwise the first row beyond
    // the cached window is treated as invalid and detail navigation stops there.
    const context = getNavigationContext();
    const pendingRequest = fetchIds(newPage);
    const version = requestVersion.current;
    return pendingRequest?.then((loaded) => {
      if (
        loaded &&
        isMounted.current &&
        version === requestVersion.current &&
        context === getNavigationContext()
      ) {
        navigateToPage(newPage);
      }
    });
  };

  return {
    rowIndex: page,
    ids,
    onPageChange,
    isInvalid,
    isLoading,
  };
}
